import { Expand, Maximize2, Minimize, Minus, Plus } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { BranchPill, GhostCard, NodeCard, StartCard } from "@/features/intake-flow/board-card";
import { useViewport } from "@/features/intake-flow/use-viewport";
import { METRICS, buildBoard, path } from "@/lib/flow-geometry";
import { cn } from "@/lib/utils";
import type { CanvasActions } from "@/features/intake-flow/actions";
import {
  type Graph,
  type Problem,
  type Question,
  addAfterNodeBlocked,
  addBeforeBlocked,
  branchOnAnswerBlocked,
  branchRemovable,
  layout,
  nodeOf,
  outOf,
  varyBySoFar,
} from "@/lib/intake-flow";

/**
 * The flow as a board (ACL-338).
 *
 * A centred trunk, one bus across the branches it fans into, and a column per branch — the shape
 * the reference draws, and the shape a tree is actually read in. A branch that splits again draws
 * its own bus in place, because the geometry recurses.
 *
 * One transformed world div holds an SVG wire layer and the cards above it. The cards stay
 * ordinary focusable HTML, so menus portal and screen readers walk them in flow order; only the
 * wires are SVG, and they carry `vectorEffect` so a hairline stays a hairline at any zoom.
 */
export function Board({
  graph,
  questions,
  problems,
  readOnly,
  actions,
  describeBranch,
}: {
  graph: Graph;
  questions: Question[];
  problems: Problem[];
  readOnly: boolean;
  actions: CanvasActions;
  /** What a branch asks, in words, for the pill's tooltip and its screen-reader line. */
  describeBranch: (key: string, position: number) => string;
}) {
  const rows = React.useMemo(() => layout(graph), [graph]);
  const board = React.useMemo(() => buildBoard(rows), [rows]);
  const trouble = React.useMemo(
    () => new Set(problems.map((p) => p.key).filter(Boolean) as string[]),
    [problems],
  );
  const { stage, view, onPointerDown, onPointerMove, onPointerUp, fit, zoomBy } = useViewport();
  const [full, setFull] = React.useState(false);

  // Escape leaves full screen, as it does everywhere else on the console.
  React.useEffect(() => {
    if (!full) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  // An empty flow draws one Start card, which is not a graph node, so `buildBoard` knows nothing
  // about it and hands back a bare padding box. Fitting to that centres the empty middle of the
  // canvas and leaves the card off to one side; this is the box the card actually occupies.
  const empty = !graph.start;
  const bounds = React.useMemo(
    () =>
      empty
        ? {
            x: -METRICS.cardW / 2 - METRICS.pad,
            y: -METRICS.pad,
            w: METRICS.cardW + METRICS.pad * 2,
            h: METRICS.cardH + METRICS.pad * 2,
          }
        : board.bounds,
    [empty, board.bounds],
  );

  // Fit once the board's shape changes, not on every render: refitting mid-edit would yank the
  // view out from under someone who had panned somewhere deliberately.
  // Full screen changes the stage's size, so it refits with the shape.
  const shape = `${bounds.w}x${bounds.h}:${board.nodes.length}:${full}`;
  const fitted = React.useRef<string>("");
  React.useEffect(() => {
    if (fitted.current === shape) return;
    const node = stage.current;
    if (!node) return;

    // Fit when the stage has a size, not merely when the shape changed.
    //
    // On the first paint the stage measures zero, so the fit was a no-op — and because the latch
    // had already been set to this shape, nothing ever fitted again and the Start card sat in
    // the corner at 100%. The latch closes only on a fit that actually happened, and a
    // ResizeObserver covers the first real layout as well as every later resize, which is what
    // going full screen is.
    if (fit(bounds)) {
      fitted.current = shape;
      return;
    }
    const watch = new ResizeObserver(() => {
      if (fit(bounds)) {
        fitted.current = shape;
        watch.disconnect();
      }
    });
    watch.observe(node);
    return () => watch.disconnect();
  }, [shape, bounds, fit, stage]);

  const numbers = React.useMemo(() => {
    const order = new Map<string, number>();
    let n = 0;
    for (const node of board.nodes) {
      if (node.kind !== "node") continue;
      n += 1;
      order.set(node.key, n);
    }
    return order;
  }, [board.nodes]);

  const subtitleOf = React.useCallback(
    (key: string) => {
      const node = nodeOf(graph, key);
      if (!node?.field_key) return null;
      return questions.find((q) => q.key === node.field_key)?.title ?? node.field_key;
    },
    [graph, questions],
  );

  if (!graph.start) {
    return (
      <Stage
        stage={stage}
        view={view}
        bounds={bounds}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onZoom={zoomBy}
        onFit={() => fit(bounds)}
        full={full}
        onFull={() => setFull((was) => !was)}
      >
        <StartCard
          at={{ kind: "node", key: "start", x: -METRICS.cardW / 2, y: 0, w: METRICS.cardW, h: METRICS.cardH, depth: 0 }}
          onAdd={readOnly ? undefined : () => actions.onAdd(null)}
        />
      </Stage>
    );
  }

  return (
    <Stage
      stage={stage}
      view={view}
      bounds={board.bounds}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onZoom={zoomBy}
      onFit={() => fit(bounds)}
      full={full}
      onFull={() => setFull((was) => !was)}
    >
      <svg
        aria-hidden
        className="pointer-events-none absolute overflow-visible"
        style={{ left: board.bounds.x, top: board.bounds.y, width: board.bounds.w, height: board.bounds.h }}
        viewBox={`${board.bounds.x} ${board.bounds.y} ${board.bounds.w} ${board.bounds.h}`}
      >
        {board.edges.map((edge, n) => (
          <path
            key={n}
            d={path(edge.points)}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            className="text-border"
          />
        ))}
      </svg>

      {board.nodes.map((at) => {
        if (at.kind === "ghost") {
          const edge = outOf(graph, at.from!).find((e) => e.position === at.position) ?? null;
          return (
            <GhostCard
              key={at.key}
              at={at}
              readOnly={readOnly}
              onAdd={() => actions.onAdd(edge ? { edge } : null)}
            />
          );
        }
        const node = nodeOf(graph, at.key);
        if (!node) return null;
        return (
          <NodeCard
            key={at.key}
            at={at}
            node={node}
            index={numbers.get(at.key) ?? 0}
            subtitle={subtitleOf(at.key)}
            invalid={trouble.has(at.key)}
            readOnly={readOnly}
            branchBlocked={branchOnAnswerBlocked(
              graph,
              at.key,
              questions.find((q) => q.key === node.field_key),
            )}
            onEdit={() => actions.onEdit(at.key)}
            onDelete={() => actions.onDelete(at.key)}
            // Named by the step, not by its edge: the last step of a chain has no edge out, and
            // "after nothing" used to mean "at the front".
            onAddAfter={() => actions.onAdd({ after: at.key })}
            afterBlocked={addAfterNodeBlocked(graph, at.key)}
            // "Before" is what puts a step above an end card every branch shares, which naming
            // an edge cannot express.
            onAddBefore={() => actions.onAdd({ before: at.key })}
            beforeBlocked={addBeforeBlocked(graph, at.key)}
            onAddBranch={() => actions.onAddBranch(at.key)}
            onBranchOnAnswer={() => actions.onBranchOnAnswer(at.key)}
            onVaryBy={() => actions.onVaryBy(at.key)}
            varied={varyBySoFar(graph, at.key) !== null}
            onAddCatchAll={
              // Offered only where it means something: a split that has no catch-all yet.
              outOf(graph, at.key).length > 1 &&
              !outOf(graph, at.key).some((e) => e.conditions.length === 0)
                ? () => actions.onAddCatchAll(at.key)
                : null
            }
          />
        );
      })}

      {board.pills.map((at) => (
        <BranchPill
          key={at.key}
          at={at}
          sentence={describeBranch(at.from, at.position)}
          readOnly={readOnly}
          removable={branchRemovable(graph, at.from, at.position)}
          onEdit={() => actions.onBranchConditions(at.from, at.position)}
          // Named by the edge, so the step lands on this path alone — the way a split whose
          // paths end in different places gains one.
          onAddHere={() => {
            const edge = outOf(graph, at.from).find((e) => e.position === at.position);
            if (edge) actions.onAdd({ edge });
          }}
          onRemove={() => actions.onRemoveBranch(at.from, at.position)}
        />
      ))}
    </Stage>
  );
}

/**
 * The transformed plane, its zoom chrome and its full-screen behaviour.
 *
 * Exported so a second board can sit on the same stage the builder does, with pan, zoom and fit
 * decided in one place (ACL-338). The rep's board was that second caller until ACL-403 replaced
 * it with one question at a time; only the builder uses this today.
 */
export function Stage({
  stage,
  view,
  bounds,
  children,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onZoom,
  onFit,
  full,
  onFull,
}: {
  stage: React.RefObject<HTMLDivElement | null>;
  view: { x: number; y: number; scale: number };
  bounds: { x: number; y: number; w: number; h: number };
  children: React.ReactNode;
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onZoom: (factor: number) => void;
  onFit: () => void;
  full: boolean;
  onFull: () => void;
}) {
  return (
    <div
      ref={stage}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="tree"
      aria-label="The intake flow"
      className={cn(
        "relative touch-none overflow-hidden border border-border bg-canvas",
        full
          ? "fixed inset-0 z-50 rounded-none"
          : "h-[clamp(420px,calc(100dvh-26rem),860px)] rounded-xl",
      )}
      style={{
        backgroundImage: "radial-gradient(var(--color-border) 1px, transparent 1px)",
        backgroundSize: "20px 20px",
        backgroundPosition: `${view.x}px ${view.y}px`,
      }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})` }}
      >
        {children}
      </div>

      <div
        data-no-pan
        className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg border border-border bg-surface/90 p-1 shadow-sm backdrop-blur"
      >
        <Button variant="ghost" size="sm" aria-label="Zoom out" onClick={() => onZoom(1 / 1.2)}>
          <Minus className="size-4" />
        </Button>
        <span className="min-w-10 text-center text-xs tabular-nums text-text-muted">
          {Math.round(view.scale * 100)}%
        </span>
        <Button variant="ghost" size="sm" aria-label="Zoom in" onClick={() => onZoom(1.2)}>
          <Plus className="size-4" />
        </Button>
        <Button variant="ghost" size="sm" aria-label="Fit the flow on screen" onClick={onFit}>
          <Maximize2 className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={full ? "Leave full screen" : "Full screen"}
          onClick={onFull}
        >
          {full ? <Minimize className="size-4" /> : <Expand className="size-4" />}
        </Button>
      </div>
      <span className="sr-only">{`The flow is ${Math.round(bounds.w)} by ${Math.round(bounds.h)} on the board.`}</span>
    </div>
  );
}
